import React, { useState, useRef, useEffect } from "react";
import Calendar from "react-calendar";
import { createPortal } from "react-dom";
import cs from "classnames";
// import "./index.scss";
import Icon from "../Icon";

type DatePickerMode = "single" | "range";

interface DatePickerProps {
  mode?: DatePickerMode;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (value: Date | [Date | null, Date | null] | null) => void;
  value?: Date | [Date | null, Date | null] | null;
  optionsClassName?: string;
  render?: (value?: Date | [Date | null, Date | null] | null) => React.ReactNode;
  minDate?: Date;
  maxDate?: Date;
}

const defaultProps: Partial<DatePickerProps> = {
  placeholder: "请选择日期",
  disabled: false,
  mode: "range"
};

const DatePicker: React.FC<DatePickerProps> = (baseProps) => {
  const props = { ...defaultProps, ...baseProps };
  const {
    placeholder,
    className,
    style,
    disabled,
    onChange,
    value,
    optionsClassName,
    render,
    mode,
    minDate,
    maxDate
  } = props;

  const isRangeMode = mode === "range";
  const [visible, setVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | [Date | null, Date | null] | null>(
    value ?? (isRangeMode ? [null, null] : null)
  );

  const pickerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value !== undefined) {
      setSelectedDate(value);
    }
  }, [value]);

  // 点击外部关闭日历
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setVisible(false);
      }
    }

    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visible]);

  const handleDateChange = (date: any) => {
    if (disabled) return;
    const result = isRangeMode ? date as [Date, Date] : Array.isArray(date) ? date[0] : date;
    setSelectedDate(result);
    onChange?.(result);
    setVisible(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    const cleared:any = isRangeMode ? [null, null] : null;
    setSelectedDate(cleared);
    onChange?.(cleared);
  };

  const [optionsStyle, setOptionsStyle] = useState<React.CSSProperties>({});
  const calculateOptionsPosition = () => {
    if (pickerRef.current && calendarRef.current) {
      const rect = pickerRef.current.getBoundingClientRect();
      const calendarHeight = calendarRef.current.offsetHeight;
      const spacing = 8;
  
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;
  
      const shouldPlaceAbove = spaceBelow < calendarHeight && spaceAbove > calendarHeight;
  
      const top = shouldPlaceAbove
        ? window.scrollY + rect.top - calendarHeight - spacing
        : window.scrollY + rect.bottom + spacing;
  
      const left = window.scrollX + rect.left;
  
      setOptionsStyle({
        top: `${top}px`,
        left: `${left}px`,
        width: `${rect.width}px`
      });
    }
  };
  
  

  useEffect(() => {
    if (visible) {
      calculateOptionsPosition();
    }
  }, [visible]);

  const prefixCls = "wm-datepicker";
  const classNames = cs(prefixCls, className);
  const classNamesOptions = cs(`${prefixCls}-options`, optionsClassName);

  const renderedContent = render
    ? render(selectedDate)
    : isRangeMode
      ? Array.isArray(selectedDate) && selectedDate[0] && selectedDate[1]
        ? `${selectedDate[0].toLocaleDateString()} - ${selectedDate[1].toLocaleDateString()}`
        : <span className="placeholder">{placeholder}</span>
      : selectedDate
        ? (selectedDate as Date).toLocaleDateString()
        : <span className="placeholder">{placeholder}</span>;

  const showClear = isRangeMode
    ? Array.isArray(selectedDate) && selectedDate[0] && selectedDate[1]
    : !!selectedDate;

  return (
    <div className={classNames} style={style} ref={pickerRef}>
      <div className="selection" onClick={() => !disabled && setVisible(!visible)}>
        {renderedContent}
        {showClear && (
          <Icon
            onClick={handleClear}
            name="icon-general-sideclose"
            className="wm-datepicker-close"
          />
        )}
      </div>

      {visible &&
        createPortal(
          <div className={classNamesOptions} ref={calendarRef} style={optionsStyle}>
            <Calendar
              nextLabel={<Icon name="icon-general-arrow-line-right" />}
              next2Label={<></>}
              prevLabel={<Icon name="icon-general-arrow-line-left" />}
              prev2Label={<></>}
              locale="en-US"
              formatMonthYear={(locale, date) => `${date.getFullYear()}年 ${date.getMonth() + 1}月`}
              formatShortWeekday={(locale, date) => ["日", "一", "二", "三", "四", "五", "六"][date.getDay()]}
              formatMonth={(locale, date) => `${date.getMonth() + 1}月`}
              selectRange={isRangeMode}
              onChange={(date)=>handleDateChange(date)}
              onViewChange={()=>{
                calculateOptionsPosition()
              }}
              value={selectedDate}
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default React.memo(DatePicker);
